import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Icon } from "semantic-ui-react";
import { useData, useViewerDispatch } from "../StateManagement";
import _ from "lodash";
import { GradientButton } from "./InternalComponents/GradientButton";
import { TransparencySlider } from "./InternalComponents/TransparencySlider";
import { DraggableWindow } from "./InternalComponents/DraggableWindow";
import { getContrastColor, lerpColor } from "../../../../../utilities/jsUtils.js";

export const DataLayerOptions = () => {
	const [popupOpen, setPopupOpen] = useState(false);

	return (
		<>
			<Button className="actionsPopupTrigger iconButton" icon onClick={() => setPopupOpen((prev) => !prev)}>
				<Tooltip
					placement="top"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					motion={{ motionName: "rc-tooltip-zoom" }}
					overlay="Options"
				>
					<Icon className="cog" size="large" style={{ width: 21, height: 21 }} />
				</Tooltip>
			</Button>
			{createPortal(<DataLayerOptionsPopup open={popupOpen} setOpen={setPopupOpen} />, document.body)}
		</>
	);
};

type PopupProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const COLOR_PALETTES: { [key: string]: string[] } = {
	by: ["#0000ff", "#9d02d7", "#cd34b5", "#ea5f94", "#fa8775", "#ffb14e", "#ffd700"],
	gfb: ["#000000", "#00a9ff", "#00ff00", "#ffff00", "#ffffff"],
	ryg: ["#ff0000", lerpColor("#ff0000", "#ffff00", 0.5), "#ffff00", lerpColor("#ffff00", "#00ff00", 0.5), "#00ff00"],
	spectral: ["#660000", "#ff0000", "#ff8800", "#ffff00", "#00ff00", "#00ffff", "#0000ff"],
	thermal: ["#000000", "#4b0082", "#ff0000", "#ffff00", "#ffffff"],
	grayscale: [
		"#000000",
		lerpColor("#000000", "#ffffff", 0.25),
		lerpColor("#000000", "#ffffff", 0.5),
		lerpColor("#000000", "#ffffff", 0.75),
		"#ffffff",
	],
};

const DataLayerOptionsPopup = ({ open, setOpen }: PopupProps) => {
	const [selectedGradient, setSelectedGradient] = useState("grayscale");
	const [layerOpacity, setLayerOpacity] = useState(0.45);

	const { dataLayerNames } = useData();
	const { updateDataLayerColorMap, updateDataLayerOpacity } = useViewerDispatch();

	// const [layerInfo, setLayerInfo] = useState<{ name: string; color1: string; color2: string; percentage: string }[]>(
	// 	[],
	// );

	const layerInfo = useMemo(() => {
		if (!dataLayerNames || dataLayerNames.length === 0) return [];
		const [, ...layerNames] = dataLayerNames;
		const palette = COLOR_PALETTES[selectedGradient];

		return _.reduce(
			layerNames,
			(acc, name, i) => {
				const n = layerNames.length;
				const paletteIndex = n <= 1 ? 0 : Math.round((i * (palette.length - 1)) / (n - 1));
				const color = palette[paletteIndex];
				let pattern = "";
				if (i >= palette.length) {
					pattern = "hash";
					if (getContrastColor(color) === "#ffffff") {
						pattern += " inverted";
					}
				}
				acc[i] = {
					name: name,
					color: color,
					pattern: pattern,
				};
				return acc;
			},
			[] as { name: string; color: string; pattern: string }[],
		);
	}, [dataLayerNames, selectedGradient]);

	useEffect(() => {
		if (layerInfo.length > 0) {
			updateDataLayerColorMap(layerInfo.map((li) => li.color));
		}
	}, [layerInfo, updateDataLayerColorMap]);

	// useEffect(() => {
	// 	const nAtPercent = (percent: number) => {
	// 		let target = 1 - percent + layerInfo.length * percent;
	// 		return target;
	// 	};
	// 	const gradient = GRADIENTS[selectedGradient];

	// 	const stops = gradient.map(({ color, stop }) => ({ color, stop: nAtPercent(stop) }));
	// 	updateDataLayerColorStops(stops);
	// }, [selectedGradient, layerInfo]);

	return open ? (
		<DraggableWindow zId="zone" title="Zone Options" open={open} setOpen={setOpen} rootStyle={{ width: 400 }}>
			<div className="dataLayerOptionsContainer">
				<table>
					<thead>
						<tr>
							<th>Id</th>
							<th>Name</th>
							<th>Color</th>
						</tr>
					</thead>
					<tbody>
						{_.map(layerInfo, (li, i) => (
							<tr>
								<td>{i + 1}</td>
								<td>{li.name}</td>
								<td
									className={li.pattern}
									style={{
										backgroundColor: li.color,
									}}
								></td>
							</tr>
						))}
					</tbody>
				</table>
				<TransparencySlider
					rootStyle={{ gridArea: "opacity" }}
					currentOpacity={layerOpacity}
					onChange={(opacity) => {
						const rounded = Math.round(opacity * 100) / 100;
						setLayerOpacity(rounded);
						updateDataLayerOpacity(rounded);
					}}
				/>
				<div className="buttonContainer">
					<GradientButton
						selected={selectedGradient === "grayscale"}
						colorStops={COLOR_PALETTES["grayscale"]}
						title="Grayscale"
						onClick={() => {
							setSelectedGradient("grayscale");
						}}
					/>
					<GradientButton
						selected={selectedGradient === "ryg"}
						colorStops={COLOR_PALETTES["ryg"]}
						title="Red-Yellow-Green"
						onClick={() => {
							setSelectedGradient("ryg");
						}}
					/>
					<GradientButton
						selected={selectedGradient === "by"}
						colorStops={COLOR_PALETTES["by"]}
						title="Blue-Yellow"
						onClick={() => {
							setSelectedGradient("by");
						}}
					/>
				</div>
			</div>
		</DraggableWindow>
	) : (
		<></>
	);
};
